import { useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  ColumnLayout,
  Container,
  FormField,
  Header,
  Input,
  Link,
  Modal,
  SpaceBetween,
  StatusIndicator,
  Table,
  Textarea,
  type TableProps,
} from '@cloudscape-design/components';
import {
  fetchLandingPages,
  fetchLandingPage,
  registerLandingPage,
  deployLandingPage,
  healthCheckLandingPage,
  type LandingPage,
  type LandingPageDetail,
  type HealthCheckResult,
} from '../api/landing-pages';

const STATUS_MAP: Record<string, { type: 'success' | 'in-progress' | 'stopped'; label: string }> = {
  active: { type: 'success', label: 'Active' },
  provisioning: { type: 'in-progress', label: 'Provisioning' },
  inactive: { type: 'stopped', label: 'Inactive' },
};

function formatDate(iso: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function LandingPages() {
  const [pages, setPages] = useState<LandingPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Register modal
  const [showRegister, setShowRegister] = useState(false);
  const [regSlug, setRegSlug] = useState('');
  const [regProductId, setRegProductId] = useState('');
  const [regDomain, setRegDomain] = useState('');
  const [regDescription, setRegDescription] = useState('');
  const [regS3Bucket, setRegS3Bucket] = useState('');
  const [regCfDistId, setRegCfDistId] = useState('');
  const [regCfDomain, setRegCfDomain] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Detail modal
  const [detailPage, setDetailPage] = useState<LandingPageDetail | null>(null);
  const [showDetail, setShowDetail] = useState(false);

  // Deploy modal
  const [showDeploy, setShowDeploy] = useState(false);
  const [deploySlug, setDeploySlug] = useState('');
  const [deploySource, setDeploySource] = useState('');
  const [deployVersion, setDeployVersion] = useState('');

  // Health check
  const [healthResults, setHealthResults] = useState<Record<string, HealthCheckResult>>({});
  const [healthLoading, setHealthLoading] = useState<string | null>(null);

  useEffect(() => { loadPages(); }, []);

  async function loadPages() {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchLandingPages();
      setPages(data.landingPages);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  async function handleRegister() {
    if (!regSlug || !regProductId) return;
    setSubmitting(true);
    try {
      await registerLandingPage({
        slug: regSlug,
        productId: regProductId,
        domain: regDomain || undefined,
        description: regDescription || undefined,
        s3Bucket: regS3Bucket || undefined,
        cfDistributionId: regCfDistId || undefined,
        cfDomainName: regCfDomain || undefined,
      });
      setShowRegister(false);
      setRegSlug(''); setRegProductId(''); setRegDomain(''); setRegDescription('');
      setRegS3Bucket(''); setRegCfDistId(''); setRegCfDomain('');
      loadPages();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to register');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleViewDetail(slug: string) {
    try {
      const detail = await fetchLandingPage(slug);
      setDetailPage(detail);
      setShowDetail(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load details');
    }
  }

  async function handleDeploy() {
    if (!deploySlug) return;
    setSubmitting(true);
    try {
      await deployLandingPage(deploySlug, {
        source: deploySource || undefined,
        version: deployVersion || undefined,
      });
      setShowDeploy(false);
      setDeploySlug(''); setDeploySource(''); setDeployVersion('');
      loadPages();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Deploy failed');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleHealthCheck(slug: string) {
    setHealthLoading(slug);
    try {
      const result = await healthCheckLandingPage(slug);
      setHealthResults((prev) => ({ ...prev, [slug]: result }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Health check failed');
    } finally {
      setHealthLoading(null);
    }
  }

  const columns: TableProps.ColumnDefinition<LandingPage>[] = [
    {
      id: 'slug',
      header: 'Slug',
      width: 140,
      cell: (item) => (
        <Button variant="inline-link" onClick={() => handleViewDetail(item.slug)}>
          {item.slug}
        </Button>
      ),
    },
    {
      id: 'domain',
      header: 'Domain',
      width: 220,
      cell: (item) => (
        <Link external href={`https://${item.domain}`}>{item.domain}</Link>
      ),
    },
    {
      id: 'product',
      header: 'Product',
      width: 120,
      cell: (item) => <Badge>{item.productId}</Badge>,
    },
    {
      id: 'status',
      header: 'Status',
      width: 120,
      cell: (item) => {
        const s = STATUS_MAP[item.status] || STATUS_MAP.inactive;
        return <StatusIndicator type={s.type}>{s.label}</StatusIndicator>;
      },
    },
    {
      id: 'version',
      header: 'Version',
      width: 120,
      cell: (item) => item.currentVersion || '—',
    },
    {
      id: 'deploys',
      header: 'Deploys',
      width: 80,
      cell: (item) => item.deployCount || 0,
    },
    {
      id: 'lastDeploy',
      header: 'Last Deploy',
      width: 160,
      cell: (item) => formatDate(item.lastDeployAt),
    },
    {
      id: 'health',
      header: 'Health',
      width: 120,
      cell: (item) => {
        const h = healthResults[item.slug];
        if (healthLoading === item.slug) return <StatusIndicator type="loading">Checking...</StatusIndicator>;
        if (!h) return <Button variant="inline-link" onClick={() => handleHealthCheck(item.slug)}>Check</Button>;
        return h.code >= 200 && h.code < 400
          ? <StatusIndicator type="success">{h.code} ({h.latencyMs}ms)</StatusIndicator>
          : <StatusIndicator type="error">{h.error || h.code}</StatusIndicator>;
      },
    },
    {
      id: 'actions',
      header: '',
      width: 100,
      cell: (item) => (
        <Button
          variant="normal"
          onClick={() => { setDeploySlug(item.slug); setShowDeploy(true); }}
        >
          Deploy
        </Button>
      ),
    },
  ];

  return (
    <SpaceBetween size="l">
      <Header
        variant="h1"
        description="Register, deploy, and monitor product landing pages across all g/d/n/a products."
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Button iconName="refresh" onClick={loadPages}>Refresh</Button>
            <Button variant="primary" onClick={() => setShowRegister(true)}>Register Landing Page</Button>
          </SpaceBetween>
        }
      >
        Landing Pages
      </Header>

      {error && <Alert type="error" dismissible onDismiss={() => setError(null)}>{error}</Alert>}

      <Table
        columnDefinitions={columns}
        items={pages}
        loading={loading}
        loadingText="Loading landing pages..."
        trackBy="slug"
        variant="full-page"
        stickyHeader
        stripedRows
        empty={
          <Box textAlign="center" padding="l">
            <Box variant="p" color="text-status-inactive">No landing pages registered yet.</Box>
            <Button variant="primary" onClick={() => setShowRegister(true)}>Register Landing Page</Button>
          </Box>
        }
        header={<Header counter={`(${pages.length})`}>All Landing Pages</Header>}
      />

      {/* Register Modal */}
      <Modal
        visible={showRegister}
        onDismiss={() => setShowRegister(false)}
        header="Register Landing Page"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setShowRegister(false)}>Cancel</Button>
              <Button variant="primary" onClick={handleRegister} loading={submitting} disabled={!regSlug || !regProductId}>Register</Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          <FormField label="Slug" description="URL-safe identifier (e.g. 'pc3', 'gdna-main')" constraintText="Lowercase, alphanumeric, hyphens only">
            <Input value={regSlug} onChange={({ detail }) => setRegSlug(detail.value)} placeholder="pc3" />
          </FormField>
          <FormField label="Product ID" description="Which product this landing page belongs to">
            <Input value={regProductId} onChange={({ detail }) => setRegProductId(detail.value)} placeholder="aeos-pc3" />
          </FormField>
          <FormField label="Base Domain" description="Leave blank for aeospc3.com">
            <Input value={regDomain} onChange={({ detail }) => setRegDomain(detail.value)} placeholder="aeospc3.com" />
          </FormField>
          <FormField label="Description">
            <Textarea value={regDescription} onChange={({ detail }) => setRegDescription(detail.value)} rows={2} />
          </FormField>
          <FormField label="S3 Bucket" description="Leave blank to auto-generate (landing-{slug})">
            <Input value={regS3Bucket} onChange={({ detail }) => setRegS3Bucket(detail.value)} placeholder="landing-pc3" />
          </FormField>
          <FormField label="CloudFront Distribution ID" description="If already provisioned">
            <Input value={regCfDistId} onChange={({ detail }) => setRegCfDistId(detail.value)} placeholder="E1234567890" />
          </FormField>
          <FormField label="CloudFront Domain" description="If already provisioned">
            <Input value={regCfDomain} onChange={({ detail }) => setRegCfDomain(detail.value)} placeholder="d1234.cloudfront.net" />
          </FormField>
        </SpaceBetween>
      </Modal>

      {/* Deploy Modal */}
      <Modal
        visible={showDeploy}
        onDismiss={() => setShowDeploy(false)}
        header={`Deploy: ${deploySlug}`}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setShowDeploy(false)}>Cancel</Button>
              <Button variant="primary" onClick={handleDeploy} loading={submitting}>Deploy</Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          <FormField label="Source (S3 Key)" description="Path to zip file in the templates bucket">
            <Input value={deploySource} onChange={({ detail }) => setDeploySource(detail.value)} placeholder="landing-pages/pc3/v1.0.0.zip" />
          </FormField>
          <FormField label="Version Label">
            <Input value={deployVersion} onChange={({ detail }) => setDeployVersion(detail.value)} placeholder="v1.0.0" />
          </FormField>
        </SpaceBetween>
      </Modal>

      {/* Detail Modal */}
      <Modal
        visible={showDetail}
        onDismiss={() => setShowDetail(false)}
        header={detailPage ? `${detailPage.slug} — ${detailPage.domain}` : 'Details'}
        size="large"
      >
        {detailPage && (
          <SpaceBetween size="m">
            <ColumnLayout columns={3} variant="text-grid">
              <SpaceBetween size="xxs">
                <Box variant="awsui-key-label">Domain</Box>
                <Link external href={`https://${detailPage.domain}`}>{detailPage.domain}</Link>
              </SpaceBetween>
              <SpaceBetween size="xxs">
                <Box variant="awsui-key-label">S3 Bucket</Box>
                <Box><code>{detailPage.s3Bucket}</code></Box>
              </SpaceBetween>
              <SpaceBetween size="xxs">
                <Box variant="awsui-key-label">CloudFront</Box>
                <Box><code>{detailPage.cfDistributionId || '—'}</code></Box>
              </SpaceBetween>
              <SpaceBetween size="xxs">
                <Box variant="awsui-key-label">Product</Box>
                <Badge>{detailPage.productId}</Badge>
              </SpaceBetween>
              <SpaceBetween size="xxs">
                <Box variant="awsui-key-label">Current Version</Box>
                <Box>{detailPage.currentVersion || '—'}</Box>
              </SpaceBetween>
              <SpaceBetween size="xxs">
                <Box variant="awsui-key-label">Total Deploys</Box>
                <Box>{detailPage.deployCount}</Box>
              </SpaceBetween>
            </ColumnLayout>

            {detailPage.deployHistory.length > 0 && (
              <Table
                columnDefinitions={[
                  { id: 'version', header: 'Version', cell: (d) => d.version, width: 150 },
                  { id: 'files', header: 'Files', cell: (d) => d.files, width: 80 },
                  { id: 'source', header: 'Source', cell: (d) => d.source, width: 200 },
                  { id: 'deployedBy', header: 'By', cell: (d) => d.deployedBy, width: 120 },
                  { id: 'deployedAt', header: 'Deployed', cell: (d) => formatDate(d.deployedAt), width: 160 },
                ]}
                items={detailPage.deployHistory}
                trackBy="deployedAt"
                header={<Header counter={`(${detailPage.deployHistory.length})`}>Deploy History</Header>}
              />
            )}
          </SpaceBetween>
        )}
      </Modal>
    </SpaceBetween>
  );
}
